import { useEffect, ReactElement } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Application, CalculationType, CalculationDetails, Calculation, CalculationDialogMode } from '../../types/domainTypes';
import { EditDialog } from '../../components/EditDialog/EditDialog';
import { Alert, AlertTitle, FormControlLabel, Radio, Typography } from '@mui/material';
import { MaxLengthTextField } from '../../components/FormFields/MaxLengthTextField';
import { useTranslation } from 'react-i18next';
import { FormRadioGroup } from '../../components/FormFields/FormRadioGroup';
import { useParams } from 'react-router-dom';
import { useGetProjectQuery } from 'redux/services/projects';

interface CalculationDialogProps {
  onClose: () => void;
  onSave: (calculationDetails: CalculationDetails) => void;
  calculation?: Calculation;
  mode: CalculationDialogMode;
}

export type CalculationFormData = {
  calculationType: CalculationType;
  application: Application | '';
  calculationName: string;
  calculationDescription: string | undefined;
};

const calculationFormDefaults: CalculationFormData = {
  calculationType: CalculationType.PanelCheck,
  application: '',
  calculationName: '',
  calculationDescription: '',
};

export function CalculationDialog({ onClose, onSave, calculation, mode }: CalculationDialogProps): ReactElement {
  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty },
  } = useForm<CalculationFormData>({ mode: 'onChange', reValidateMode: 'onChange', defaultValues: calculationFormDefaults });
  const { t } = useTranslation();
  const calculationDetails = calculation?.details;
  const { projectId } = useParams<'projectId'>();
  const { data } = useGetProjectQuery(projectId || '');
  const calculationNames = data?.calculations.reduce((filtered: string[], calculationItem) => {
    const calculationId = calculationItem.id;
    const calculationName = calculationItem.details.name;

    if (calculationId !== calculation?.id) {
      filtered.push(calculationName);
    }
    return filtered;
  }, []);

  const validateCalculationName = (newName: string): boolean => {
    return !calculationNames?.includes(newName.trim());
  };

  const handleCalculationDialogSave: SubmitHandler<CalculationFormData> = (formData: CalculationFormData) => {
    const newCalculationDetails = {
      name: formData.calculationName.trim(),
      description: formData.calculationDescription?.trim(),
      type: formData.calculationType,
      application: formData.application,
    } as CalculationDetails;
    onSave(newCalculationDetails);
  };

  useEffect(() => {
    if (calculationDetails) {
      setValue('calculationName', calculationDetails.name);
      setValue('calculationDescription', calculationDetails.description);
      setValue('application', calculationDetails.application);
      setValue('calculationType', calculationDetails.type);
    }
  }, [calculationDetails, setValue]);

  return (
    <EditDialog
      saveActionText={
        mode !== CalculationDialogMode.Add
          ? t('common.edit-dialog.action-save')
          : t('project-details-page.calculation-dialog.save-button-text')
      }
      title={t(`project-details-page.calculation-dialog.${mode}-title`)}
      onClose={onClose}
      handleFormSubmit={handleSubmit(handleCalculationDialogSave)}
      defaultErrorMessage={t('project-details-page.calculation-dialog.error-message-save')}
      isDirty={isDirty}
      saveActionTestId="dialogAddCalculationButton"
    >
      {mode === CalculationDialogMode.Duplicate && (
        <Alert severity="info">
          <AlertTitle>
            <Typography fontFamily="KingspanBrownTT-Bold">
              {t('project-details-page.calculation-dialog.duplicate-mode-title', { calculationName: calculationDetails?.name })}
            </Typography>
          </AlertTitle>
        </Alert>
      )}
      {mode !== CalculationDialogMode.Add && (
        <Alert severity="info">
          <AlertTitle>
            <Typography fontFamily="KingspanBrownTT-Bold">{t('project-details-page.calculation-dialog.edit-mode-alert-title')}</Typography>
          </AlertTitle>
          {t('project-details-page.calculation-dialog.edit-mode-alert-caption')}
        </Alert>
      )}
      <FormRadioGroup
        control={control}
        controlName="calculationType"
        formLabel={t('project-details-page.calculation-dialog.label-calculation-type')}
        aria-label="calculation-type"
        defaultValue={calculationDetails?.type}
        name="calculation-type-group"
      >
        <FormControlLabel
          disabled={mode !== CalculationDialogMode.Add}
          value={CalculationType.PanelCheck}
          control={<Radio />}
          label={t('project-details-page.calculation-dialog.label-panel-check')}
        />
        <FormControlLabel
          disabled={mode !== CalculationDialogMode.Add}
          value={CalculationType.FastenerCheck}
          control={<Radio />}
          label={t('project-details-page.calculation-dialog.label-fastener-check')}
        />
      </FormRadioGroup>

      <FormRadioGroup
        control={control}
        controlName="application"
        required
        formLabel={t('project-details-page.calculation-dialog.label-application')}
        aria-label="application"
        defaultValue={calculationDetails?.application}
        name="application-group"
      >
        <FormControlLabel
          disabled={mode !== CalculationDialogMode.Add}
          value={Application.WallPanel}
          control={<Radio />}
          label={t('project-details-page.calculation-dialog.label-wall-panel')}
          data-testid="radio-option-wall-panel"
        />
        <FormControlLabel
          disabled={mode !== CalculationDialogMode.Add}
          value={Application.RoofPanel}
          control={<Radio />}
          label={t('project-details-page.calculation-dialog.label-roof-panel')}
        />
      </FormRadioGroup>

      <MaxLengthTextField
        name="calculationName"
        control={control}
        required
        label={t('project-details-page.calculation-dialog.label-calculation-name')}
        id="calculation-form-calculation-name"
        data-testid="calculation-form-calculation-name"
        fullWidth
        maxLength={100}
        multiline={false}
        validateCalculationName={validateCalculationName}
        autoFocus={true}
      />
      <MaxLengthTextField
        name="calculationDescription"
        control={control}
        label={t('project-details-page.calculation-dialog.label-calculation-description')}
        id="calculation-form-calculation-description"
        data-testid="calculation-form-calculation-description"
        fullWidth
        maxLength={100}
        multiline
      />
    </EditDialog>
  );
}
